import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiRequest } from '@/lib/queryClient';

interface UsageBucket {
  turns: number;
  failed: number;
  stopped: number;
  degraded: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  costPerTurnUsd: number;
  avgDurationMs: number | null;
}

export interface AIVIAUsage {
  range: string;
  total: UsageBucket;
  byDay: Array<UsageBucket & { day: string }>;
  byVariant: Array<UsageBucket & { variant: string }>;
  byAdvisor: Array<UsageBucket & { userId: number; name: string }>;
  pricing: string;
}

const usd = (v: number, digits = 2) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
const secs = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toFixed(1)} s`);
const pct = (n: number, d: number) => (d ? `${((n / d) * 100).toFixed(1)}%` : '—');
const tokens = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

function Row({ label, b }: { label: string; b: UsageBucket }) {
  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right">{b.turns}</TableCell>
      <TableCell className="text-right">{pct(b.failed, b.turns)}</TableCell>
      <TableCell className="text-right">{secs(b.avgDurationMs)}</TableCell>
      <TableCell className="text-right">{tokens(b.inputTokens)} / {tokens(b.outputTokens)}</TableCell>
      <TableCell className="text-right">{usd(b.costPerTurnUsd, 4)}</TableCell>
      <TableCell className="text-right">{usd(b.costUsd)}</TableCell>
    </TableRow>
  );
}

function UsageTable({ title, rows }: { title: string; rows: Array<{ label: string; b: UsageBucket }> }) {
  if (!rows.length) return null;
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-muted-foreground">{title}</h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead />
            <TableHead className="text-right">Turns</TableHead>
            <TableHead className="text-right">Failed</TableHead>
            <TableHead className="text-right">Avg time</TableHead>
            <TableHead className="text-right">Tokens in / out</TableHead>
            <TableHead className="text-right">Cost / turn</TableHead>
            <TableHead className="text-right">Cost</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <Row key={r.label} label={r.label} b={r.b} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** AIVIA (Threads on AgentCore): turns, failures, latency and estimated cost by A/B variant, day and advisor. */
export function AIVIAUsageSection({ range }: { range: '24h' | '7d' | '30d' }) {
  const { data, isLoading, error } = useQuery<AIVIAUsage>({
    queryKey: ['aivia-usage', range],
    queryFn: async () => {
      const body: any = await apiRequest('GET', `/api/threads-agent/admin/usage?range=${range}`);
      if (!body?.total || !Array.isArray(body.byDay) || !Array.isArray(body.byVariant) || !Array.isArray(body.byAdvisor)) {
        throw new Error('Unexpected usage response');
      }
      return body as AIVIAUsage;
    },
    staleTime: 30_000,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>AIVIA assistant (Threads on AgentCore)</CardTitle>
        <CardDescription>{data?.pricing ?? 'Estimated cost per turn and advisor.'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
        ) : error || !data ? (
          <p className="text-sm text-muted-foreground">AIVIA assistant usage is unavailable.</p>
        ) : !data.total.turns ? (
          <p className="text-sm text-muted-foreground">No AIVIA assistant answers in this period.</p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-4">
              {[
                ['Answers', String(data.total.turns)],
                ['Estimated cost', usd(data.total.costUsd)],
                ['Cost per answer', usd(data.total.costPerTurnUsd, 4)],
                ['Failed', pct(data.total.failed, data.total.turns)],
              ].map(([k, v]) => (
                <div key={k} className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">{k}</p>
                  <p className="mt-1 text-xl font-semibold">{v}</p>
                </div>
              ))}
            </div>
            <UsageTable title="By A/B variant" rows={data.byVariant.map((b) => ({ label: b.variant, b }))} />
            <UsageTable title="By day" rows={data.byDay.map((b) => ({ label: b.day, b }))} />
            <UsageTable title="By advisor (top 50 by cost)" rows={data.byAdvisor.map((b) => ({ label: b.name, b }))} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
